/**
 * @file ApplicationDetailsView.tsx
 * @description 案件「申請明細」只讀渲染（TOP / TAP / LEGACY 兼容，客戶 PPT S9/S10）。
 *
 * 從 cases.application_details（jsonb 原始值）經 @zhcb/shared parseApplicationDetails
 * 兼容解析後分流渲染：
 * - TOP：驗證品項 / 驗證範圍（各含附件數）、驗證面積、場所地址快照；
 * - TAP：5 欄明細表（產品類別/品項/作物產期/申請面積/合理採收量）+ 驗證總面積
 *   （4 位小數公頃）+ 生產地筆數 / 場區筆數 + 場所地址快照；（序 56 拆兩欄）
 * - LEGACY：舊結構原樣渲染（驗證選項/輔導員/舊三面積欄/銷售通路）。
 *
 * 共用於：案件詳情頁（/dispatch/:caseId 基本資料卡）與合約評審快照
 * （ScopeSnapshotView 的新結構補充區塊）。
 */

import { Descriptions, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  parseApplicationDetails,
  type ApplicationDetails,
  type ApplicationDetailsTap,
  type ApplicationDetailsTop,
  type CaseProductEntry,
  type TapApplicationRow,
} from '@zhcb/shared';

/** 缺值統一顯示符號 */
const DASH = '—';

/** 驗證選項 value → 中文標籤 */
const VERIFICATION_OPTION_LABELS: Record<string, string> = {
  individual: '個別驗證',
  group: '集團驗證',
};

/** 銷售對象 / 通路 value → 中文標籤（與新增案件表單一致） */
const SALES_CHANNEL_LABELS: Record<string, string> = {
  auction: '拍賣市場',
  supermarket: '超市賣場',
  processor: '加工業者',
  distributor: '分裝／流通業者',
  other: '其他銷售對象',
};

/** 空字串 / null / undefined 統一顯示 DASH */
function text(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return DASH;
  const s = String(v).trim();
  return s ? s : DASH;
}

/** 產品條目（品項/範圍）顯示：名稱 + 附件數（有附件時） */
function productEntryText(entry: CaseProductEntry): string {
  const count = entry.attachmentFileIds?.length ?? 0;
  return count > 0 ? `${entry.name}（附件 ${count} 件）` : entry.name;
}

/** 地址快照清單（多行顯示，空清單顯示 DASH） */
function AddressList({ addresses }: { addresses?: string[] }) {
  if (!addresses?.length) return <>{DASH}</>;
  return (
    <>
      {addresses.map((addr, i) => (
        <div key={i}>{addr}</div>
      ))}
    </>
  );
}

/**
 * 通用欄位（LEGACY 基底，TOP/TAP 繼承）：驗證選項 / 輔導員 / 銷售通路。
 * 舊三面積欄（applyAreaHa 等）僅 LEGACY 顯示（新結構改用 verificationArea/totalArea）。
 */
function commonItems(d: ApplicationDetails, opts: { withLegacyAreas: boolean }) {
  const items: { label: string; content: React.ReactNode; span?: number }[] = [];

  if (d.verificationOption) {
    const label = VERIFICATION_OPTION_LABELS[d.verificationOption] ?? d.verificationOption;
    items.push({
      label: '驗證選項',
      content:
        d.verificationOption === 'group' && d.groupFarmerCount
          ? `${label}（農戶數 ${d.groupFarmerCount} 戶）`
          : label,
    });
  }
  if (d.counselorName || d.counselorPhone) {
    items.push({
      label: '輔導員',
      content: [d.counselorName, d.counselorPhone].filter(Boolean).join(' ／ ') || DASH,
    });
  }
  if (opts.withLegacyAreas) {
    const areaParts: string[] = [];
    if (d.applyAreaHa !== undefined) areaParts.push(`申請驗證面積 ${d.applyAreaHa} 公頃`);
    if (d.organicAreaHa !== undefined) areaParts.push(`有機面積 ${d.organicAreaHa} 公頃`);
    if (d.organicConversionAreaHa !== undefined)
      areaParts.push(`有機轉型期面積 ${d.organicConversionAreaHa} 公頃`);
    if (areaParts.length) items.push({ label: '驗證面積', content: areaParts.join('、'), span: 2 });
  }
  if (d.salesChannels?.length) {
    const channelNames: Record<string, string | undefined> = {
      auction: d.salesAuctionName,
      supermarket: d.salesSupermarketName,
      processor: d.salesProcessorName,
      distributor: d.salesDistributorName,
      other: d.salesOtherName,
    };
    items.push({
      label: '產品銷售對象／通路',
      span: 2,
      content: d.salesChannels
        .map((ch) => {
          const label = SALES_CHANNEL_LABELS[ch] ?? ch;
          const name = channelNames[ch]?.trim();
          return name ? `${label}（${name}）` : label;
        })
        .join('、'),
    });
  }
  return items;
}

/** 場所地址快照兩行（TOP / TAP 共用） */
function siteSnapshotItems(d: ApplicationDetailsTop | ApplicationDetailsTap) {
  return [
    {
      label: '驗證場所地址',
      span: 2,
      content: <AddressList addresses={d.landAddressSnapshot} />,
    },
    {
      label: '採收後處理場所地址',
      span: 2,
      content: <AddressList addresses={d.siteAddressSnapshot} />,
    },
  ];
}

/** TAP 明細表列定義（5 欄 + 附件數） */
const TAP_COLUMNS: ColumnsType<TapApplicationRow> = [
  { title: '產品類別', dataIndex: 'productCategory', render: (v: string) => text(v) },
  { title: '產品品項', dataIndex: 'productItem', render: (v: string) => text(v) },
  { title: '作物產期', dataIndex: 'cropPeriod', render: (v?: string) => text(v) },
  {
    title: '申請面積（公頃）',
    dataIndex: 'appliedArea',
    align: 'right',
    render: (v: string) => text(v),
  },
  { title: '合理採收量', dataIndex: 'reasonableYield', render: (v?: string) => text(v) },
  {
    title: '附件',
    dataIndex: 'attachmentFileIds',
    width: 72,
    render: (ids?: number[]) => (ids?.length ? `${ids.length} 件` : DASH),
  },
];

interface ApplicationDetailsViewProps {
  /** cases.application_details 的 jsonb 原始值（可為 null / 舊結構 / 新結構） */
  details: unknown;
  /**
   * 是否渲染通用欄位（驗證選項/輔導員/銷售通路）。
   * 合約評審快照頁已在主 Descriptions 顯示過這些欄位，可關閉避免重複。
   */
  showCommonFields?: boolean;
}

/**
 * 申請明細只讀渲染。details 解析為 null（未填 / 非物件）時返回 null，由呼叫端決定佔位。
 */
export function ApplicationDetailsView({
  details,
  showCommonFields = true,
}: ApplicationDetailsViewProps) {
  const parsed = parseApplicationDetails(details);
  if (!parsed) return null;

  if (parsed.kind === 'TAP') {
    const d = parsed.details;
    return (
      <div>
        <Table<TapApplicationRow>
          size="small"
          rowKey={(_, i) => String(i)}
          columns={TAP_COLUMNS}
          dataSource={d.rows}
          pagination={false}
          style={{ marginBottom: 12 }}
        />
        <Descriptions size="small" column={2} bordered>
          {/* 序 56：生產地及場區筆數拆兩欄，排序 生產地筆數 → 場區筆數 → 驗證總面積 */}
          <Descriptions.Item label="生產地筆數">{text(d.landCount)}</Descriptions.Item>
          <Descriptions.Item label="場區筆數">{text(d.siteCount)}</Descriptions.Item>
          <Descriptions.Item label="驗證總面積">
            <Typography.Text strong>{d.totalArea}</Typography.Text> 公頃
          </Descriptions.Item>
          {siteSnapshotItems(d).map((it) => (
            <Descriptions.Item key={it.label} label={it.label} span={it.span}>
              {it.content}
            </Descriptions.Item>
          ))}
          {showCommonFields &&
            commonItems(d, { withLegacyAreas: false }).map((it) => (
              <Descriptions.Item key={it.label} label={it.label} span={it.span}>
                {it.content}
              </Descriptions.Item>
            ))}
        </Descriptions>
      </div>
    );
  }

  if (parsed.kind === 'TOP') {
    const d = parsed.details;
    return (
      <Descriptions size="small" column={2} bordered>
        {/* 2026-09-11：有機一覽表的層級是 類別→品項→產品範圍，本欄填的是「品項」層，
            故隨申請明細表單一併正名為「驗證品項」（TAP 的品項層另在上方表格） */}
        <Descriptions.Item label="驗證品項" span={2}>
          {d.productItems?.length
            ? d.productItems.map((it, i) => <Tag key={i}>{productEntryText(it)}</Tag>)
            : DASH}
        </Descriptions.Item>
        {/* 序100：產品範圍改名「驗證範圍」；≥3 個時預設展示前 3 個＋「等 N 項」摘要 */}
        <Descriptions.Item label="驗證範圍" span={2}>
          {d.productScopes?.length ? (
            <>
              {d.productScopes.slice(0, 3).map((it, i) => (
                <Tag key={i}>{productEntryText(it)}</Tag>
              ))}
              {d.productScopes.length > 3 && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  等 {d.productScopes.length} 項（詳見申請表附件）
                </Typography.Text>
              )}
            </>
          ) : (
            DASH
          )}
        </Descriptions.Item>
        <Descriptions.Item label="驗證面積" span={2}>
          {d.verificationArea ? (
            <>
              <Typography.Text strong>{d.verificationArea}</Typography.Text> 公頃
            </>
          ) : (
            DASH
          )}
        </Descriptions.Item>
        {siteSnapshotItems(d).map((it) => (
          <Descriptions.Item key={it.label} label={it.label} span={it.span}>
            {it.content}
          </Descriptions.Item>
        ))}
        {showCommonFields &&
          commonItems(d, { withLegacyAreas: false }).map((it) => (
            <Descriptions.Item key={it.label} label={it.label} span={it.span}>
              {it.content}
            </Descriptions.Item>
          ))}
      </Descriptions>
    );
  }

  // LEGACY：舊結構原樣渲染（含舊三面積欄）；全部欄位皆空時顯示提示
  const legacyItems = commonItems(parsed.details, { withLegacyAreas: true });
  if (legacyItems.length === 0) {
    return <Typography.Text type="secondary">（無申請明細）</Typography.Text>;
  }
  return (
    <Descriptions size="small" column={2} bordered>
      {legacyItems.map((it) => (
        <Descriptions.Item key={it.label} label={it.label} span={it.span}>
          {it.content}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
}
